import { InputHTMLAttributes, forwardRef } from "react";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import useA11y from "apps/website/hooks/useA11y";
import cx from "apps/website/utils/misc/cx";
import {
  Padding,
  paddingXMap,
  paddingYMap,
} from "apps/website/maps/Spacing.map";
import { Gap, gapMap } from "apps/website/maps/Grid.map";
import { Align } from "apps/website/components/base/Text/types";
import { alignVariants } from "apps/website/components/base/Text/constant";
import Icon from "apps/website/components/base/Icon/Icon";
import textSubtitleStyles, {
  TextSubtitleVariantsProps,
} from "apps/website/components/base/Text/TextSubtitle/styles";

import { IErrorProps } from "../../Error/types";

import { Size, sizeMap } from "./InputField.map";
import {
  TextBodyVariantsProps,
} from "apps/website/components/base/Text/TextBody/styles";

// Export reusable base classes for input field styling using design system
export const getInputFieldBaseClasses = (textSize: TextSubtitleVariantsProps["size"] = 3) => {
  const textStyles = textSubtitleStyles({ size: textSize });
  return `bg-white ${textStyles} no-spinner w-full appearance-none border-2 border-outline-grey rounded-xl focus:outline-none placeholder:text-graphite-grey`;
};

type LabelProps =
  | { label: string; srLabel?: never }
  | { label?: never; srLabel: string };

export interface IStyleOptions {
  paddingX?: Padding;
  paddingY?: Padding;
  textAlign?: Align
  inputTextSize?: TextSubtitleVariantsProps["size"]
  labelTextSize?: TextBodyVariantsProps["size"]
  labelGap?: Gap
}

export type IInputField = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & LabelProps & {
  name: string;
  error?: boolean;
  errorMessage?: string;
  size?: Size
  styleOptions?:IStyleOptions
  component?: string;
  isValid?: boolean;
};

const ErrorMessage = ({ error, errorMessage, id }: IErrorProps) => {
  if (!errorMessage || !error) { return null; }

  return (
    <TextBody className="text-red" size={3} id={`${id}-error`}>
      { errorMessage }
    </TextBody>
  );
};

export const InputField = forwardRef<HTMLInputElement, IInputField>(({
  name,
  label,
  error,
  errorMessage,
  size = "default",
  placeholder,
  srLabel,
  styleOptions,
  component = "InputField",
  isValid,
  ...props
}, ref) => {
  const { UUID } = useA11y();

  const { paddingX = 4, paddingY = 3, textAlign = "default", inputTextSize = 3, labelTextSize = 3, labelGap = 2 } = styleOptions ?? {};

  const computedClass = cx(
    getInputFieldBaseClasses(inputTextSize),
    error && "border-red border-spacing-0.5",
    alignVariants[textAlign],
    paddingXMap[paddingX],
    paddingYMap[paddingY],
  );

  return (
    <div className={cx(sizeMap[size])} data-component={component}>
      <label htmlFor={UUID}>
        <div className={cx("flex flex-col", gapMap[labelGap])}>

          <TextBody size={labelTextSize} align={textAlign} className={cx("!mb-0", !label && "sr-only")}>
            { label ?? srLabel }
          </TextBody>

          <div className="relative">
            <input
              ref={ref}
              id={UUID}
              name={name}
              placeholder={placeholder}
              aria-describedby={error ? `${UUID}-error` : undefined}
              className={computedClass}
              onWheel={(e) => {
                if (e.target instanceof HTMLInputElement && e.target.type === "number") {
                  e.target.blur();
                  e.preventDefault();
                }
              }}
              {...props}
            />
            { isValid && (
              <Icon
                size="xsmall"
                icon="tickHeavy"
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-green"
              />
            ) }
          </div>

          <ErrorMessage error={error} errorMessage={errorMessage} id={UUID} />

        </div>
      </label>
    </div>
  );
});

InputField.displayName = "InputField";
